import { FC, MouseEvent, ReactNode, RefObject } from 'react'
import { composeClasses } from '@/utils/compose-classes'
import { Spinner } from '@/components/spinner'

export interface PillButtonProps {
  children?: ReactNode
  onClick?: (event: MouseEvent) => void
  isLoading?: boolean
  contentLoading?: ReactNode
  isDisabled?: boolean
  className?: string
  isActive?: boolean
  size?: 'medium'
  rightIcon?: ReactNode
  leftIcon?: ReactNode
  onlyIcon?: ReactNode
  ref?: RefObject<HTMLButtonElement>
}

const assignedSize = {
  medium: 'text-body-bold-m'
} as const

export const PillButton: FC<PillButtonProps> = ({
  children,
  onClick,
  isLoading = false,
  isDisabled = false,
  contentLoading,
  className = '',
  isActive,
  size = 'medium',
  onlyIcon,
  rightIcon,
  leftIcon,
  ref
}) => {
  if (!onlyIcon && !children) {
    throw new Error('PillButton must have children or onlyIcon prop')
  }

  const sizeClass = assignedSize[size]

  return (
    <button
      ref={ref}
      onClick={onClick}
      type="button"
      className={composeClasses(
        className,
        isActive && 'bg-surface-dark text-white border-transparent',
        !isActive &&
          'text-primary bg-white border-surface-strokes hover:border-primary active:bg-surface-gray-10',
        onlyIcon ? 'p-[13px]' : 'px-4 py-[11px]',
        sizeClass,
        (isDisabled || isLoading) && 'pointer-events-none cursor-not-allowed',
        'border border-solid gap-1 flex items-center justify-center duration-200 ease-out transition-colors whitespace-nowrap flex-row rounded-full disabled:text-terciary disabled:bg-button-disabled'
      )}
      disabled={isDisabled}
    >
      {isLoading
        ? contentLoading ?? (
            <Spinner
              customBorderColor={
                isDisabled ? 'border-terciary' : 'border-primary'
              }
            />
          )
        : onlyIcon ?? (
            <PillButtonContent leftIcon={leftIcon} rightIcon={rightIcon}>
              {children}
            </PillButtonContent>
          )}
    </button>
  )
}

type PillButtonContentProps = Pick<
  PillButtonProps,
  'leftIcon' | 'rightIcon' | 'children'
>

const PillButtonContent = (props: PillButtonContentProps) => {
  const { leftIcon, rightIcon, children } = props
  return (
    <>
      {leftIcon && <>{leftIcon}</>}
      {children}
      {rightIcon && <>{rightIcon}</>}
    </>
  )
}

export default PillButton
